import { type ApiClient, ApiError, isNetworkError } from "@ronin/api-client";
import type { Note, UpdateNote } from "@ronin/contracts";
import { type OutboxEntry, versionCompatibility } from "@ronin/core";
import type { Conflict, NotebookEntry, OfflineStore } from "./store.js";

export type SyncResult =
  | {
      kind: "ok";
      sent: number;
      conflicts: Conflict[];
      failed: number;
      /** FR9: notebook changes the server refused (dropped), with its messages. */
      refused: string[];
    }
  /** The server could not be reached; everything stays queued. */
  | { kind: "offline" }
  /** FR17: the session ended (or belongs to someone else); store and outbox are kept. */
  | { kind: "unauthorized" }
  /** FR7 step 1: app and server versions differ; nothing was sent. */
  | { kind: "incompatible" }
  /** 5xx or rate limit: retried at the next sync. */
  | { kind: "error" };

const status = (error: unknown) => (error instanceof ApiError ? error.status : 0);
const message = (error: unknown) =>
  error instanceof Error ? error.message : "Refused by the server";

/** Errors that stop the whole sync; anything else only fails one entry (FR11). */
const stopsSync = (error: unknown) =>
  isNetworkError(error) || status(error) === 401 || status(error) === 429 || status(error) >= 500;

/** Runs `call`; a 404 means it is already in the wanted state (FR9: trash), so it is fine. */
async function allowMissing(call: () => Promise<unknown>) {
  try {
    await call();
  } catch (error) {
    if (status(error) !== 404) throw error;
  }
}

/**
 * One sync (FR7): check the session and versions, push the outbox (notebooks first, then
 * notes, then notebook deletes), then pull. Never runs twice at once (index.ts).
 */
export function createSync(deps: { api: ApiClient; store: OfflineStore; appVersion: string }) {
  const { api, store } = deps;

  // --- Notes ---

  /** A create; a notebook deleted meanwhile puts the note into "No notebook" (FR9). */
  async function create(entry: OutboxEntry): Promise<Note> {
    const { kind, title, content, notebookId, pinned, deleted } = entry.fields;
    const input = { id: entry.id, kind, title, content, notebookId };
    let note: Note;
    try {
      note = await api.notes.create(input);
    } catch (error) {
      if (status(error) !== 404 || !notebookId) throw error;
      note = await api.notes.create({ ...input, notebookId: null });
    }
    if (pinned && !note.pinned) note = await api.notes.update(note.id, { pinned: true });
    if (deleted) await allowMissing(() => api.notes.remove(entry.id));
    return note;
  }

  async function update(entry: OutboxEntry): Promise<Note | null> {
    const { title, content, pinned, notebookId, deleted } = entry.fields;
    // Restore first: a note in the trash cannot be changed.
    if (deleted === false) await allowMissing(() => api.notes.restore(entry.id));
    const patch: UpdateNote = {
      ...(title !== undefined && { title }),
      ...(pinned !== undefined && { pinned }),
      ...(notebookId !== undefined && { notebookId }),
      ...(content !== undefined && {
        content,
        baseVersion: entry.baseVersion ?? store.note(entry.id)?.version ?? 1,
      }),
    };
    let saved: Note | null = null;
    if (Object.keys(patch).length > 0) {
      try {
        saved = await api.notes.update(entry.id, patch);
      } catch (error) {
        // 404 with a target notebook: the notebook may be the one that is gone (FR9).
        if (status(error) !== 404 || !patch.notebookId) throw error;
        saved = await api.notes.update(entry.id, { ...patch, notebookId: null });
      }
    }
    if (deleted === true) await allowMissing(() => api.notes.remove(entry.id));
    return saved;
  }

  /**
   * "sent", "skipped" (nothing reached the server: a re-created note is counted when its
   * copy is sent) or the conflict pair when the server had a newer version (FR8).
   */
  async function push(entry: OutboxEntry): Promise<"sent" | "skipped" | Conflict | null> {
    if (entry.op === "destroy") {
      await allowMissing(() => api.notes.remove(entry.id));
      await allowMissing(() => api.notes.destroy("note", entry.id));
      store.settleEntry(entry);
      return "sent";
    }
    if (entry.op === "create") {
      store.confirmPush(entry, await create(entry));
      return "sent";
    }
    try {
      const saved = await update(entry);
      if (saved) store.confirmPush(entry, saved);
      else store.settleEntry(entry);
      return "sent";
    } catch (error) {
      if (status(error) === 409) return store.conflict(entry);
      if (status(error) !== 404) throw error;
      // Gone on the server (trash or purged). Content or title edits live on as a new note.
      const edited = entry.fields.content !== undefined || entry.fields.title !== undefined;
      if (edited) store.recreate(entry);
      else store.settleEntry(entry);
      return "skipped";
    }
  }

  // --- Notebooks (FR6a) ---

  /** Creates, renames, moves and restores: before the notes, which may point at them. */
  async function notebookEarly(entry: NotebookEntry) {
    const { name, parentId, deleted } = entry.fields;
    if (entry.op === "create") {
      try {
        await api.notes.createNotebook({ id: entry.id, name: name ?? "", parentId });
      } catch (error) {
        if (status(error) !== 404 || !parentId) throw error;
        await api.notes.createNotebook({ id: entry.id, name: name ?? "", parentId: null });
      }
      return;
    }
    if (entry.op !== "update") return;
    if (deleted === false) await allowMissing(() => api.notes.restoreNotebook(entry.id));
    const patch = {
      ...(name !== undefined && { name }),
      ...(parentId !== undefined && { parentId }),
    };
    if (Object.keys(patch).length > 0) await api.notes.updateNotebook(entry.id, patch);
  }

  /** Deletes to the trash and for good: after the notes. */
  async function notebookLate(entry: NotebookEntry) {
    if (entry.op === "destroy") {
      await allowMissing(() => api.notes.removeNotebook(entry.id));
      await allowMissing(() => api.notes.destroy("notebook", entry.id));
    } else if (entry.fields.deleted === true) {
      await allowMissing(() => api.notes.removeNotebook(entry.id));
    }
  }

  async function pull() {
    const since = store.meta()?.lastSync ?? undefined;
    let cursor: string | undefined;
    let serverTime: string | undefined;
    do {
      const page = await api.notes.sync({ since, cursor });
      serverTime ??= page.serverTime;
      store.applyPull(page);
      cursor = page.nextCursor ?? undefined;
    } while (cursor);
    for (const note of store.missingContent()) {
      try {
        store.applyContent(await api.notes.get(note.id));
      } catch (error) {
        if (status(error) !== 404) throw error;
      }
    }
    if (serverTime) store.setLastSync(serverTime);
  }

  return async function sync(): Promise<SyncResult> {
    try {
      const session = await api.auth.session();
      const meta = store.meta();
      if (!session.user || !meta || session.user.id !== meta.user.id) {
        return { kind: "unauthorized" };
      }
      if (session.version && versionCompatibility(deps.appVersion, session.version) !== "ok") {
        return { kind: "incompatible" };
      }

      let sent = 0;
      let failed = 0;
      const conflicts: Conflict[] = [];
      const refused: string[] = [];
      const notebookEntries = store.notebookEntries();
      const dropped = new Set<string>();

      /** A refused notebook change is dropped (FR9); the pull shows the server's state. */
      const forNotebooks = async (step: (entry: NotebookEntry) => Promise<void>) => {
        for (const entry of notebookEntries) {
          if (dropped.has(entry.id)) continue;
          try {
            await step(entry);
          } catch (error) {
            if (stopsSync(error)) throw error;
            dropped.add(entry.id);
            refused.push(message(error));
            store.settleNotebook(entry);
          }
        }
      };

      await forNotebooks(notebookEarly);

      // Entries added while pushing (conflict copies, re-created notes) go out in this run.
      for (let entries = store.pendingEntries(); entries.length > 0; ) {
        const done = new Set<string>();
        for (const entry of entries) {
          done.add(`${entry.id}:${entry.rev}`);
          try {
            const outcome = await push(entry);
            if (outcome === "sent") sent++;
            else if (outcome && outcome !== "skipped") conflicts.push(outcome);
          } catch (error) {
            if (stopsSync(error)) throw error;
            store.failEntry(entry, message(error));
            failed++;
          }
        }
        entries = store.pendingEntries().filter((e) => !done.has(`${e.id}:${e.rev}`));
      }

      await forNotebooks(notebookLate);
      for (const entry of notebookEntries) {
        if (dropped.has(entry.id)) continue;
        store.settleNotebook(entry);
        sent++;
      }

      await pull();
      return { kind: "ok", sent, conflicts, failed, refused };
    } catch (error) {
      if (isNetworkError(error)) return { kind: "offline" };
      if (status(error) === 401) return { kind: "unauthorized" };
      return { kind: "error" };
    }
  };
}
